export const DEFAULT_COGO_IDENTITY_PROXY_BASE = 'https://identity-dgx-dev.cogo.xyz'
export const DEFAULT_COGO_PLATFORM_PROXY_BASE = '/api/platform'
export const DEFAULT_COGO_PLATFORM_BASE = 'https://platform.cogo.xyz'

export function readStudioEnv(name: string): string | undefined {
  const viteValue = (import.meta as ImportMeta & { env?: Record<string, string | undefined> })
    .env?.[name]
  if (viteValue !== undefined) return viteValue
  return typeof process !== 'undefined' ? process.env[name] : undefined
}

export function getStudioAuthProvider(): string {
  return (readStudioEnv('VITE_AUTH_PROVIDER') ?? 'supabase').toLowerCase()
}

export function getCogoIdentityBase(): string {
  return stripTrailingSlash(
    readStudioEnv('VITE_COGO_IDENTITY_API_URL') ?? DEFAULT_COGO_IDENTITY_PROXY_BASE,
  )
}

export function getCogoPlatformBase(): string {
  const authProvider = getStudioAuthProvider()
  const defaultBase = authProvider === 'cogo' ? DEFAULT_COGO_PLATFORM_PROXY_BASE : DEFAULT_COGO_PLATFORM_BASE
  const legacyCloudflareBase =
    authProvider === 'cogo' ? undefined : readStudioEnv('VITE_COGO_CLOUDFLARE_URL')
  return stripTrailingSlash(
    readStudioEnv('VITE_PLATFORM_BASE') ?? legacyCloudflareBase ?? defaultBase,
  )
}

export function cogoPlatformApiUrl(path: string): string {
  const suffix = path.startsWith('/') ? path : `/${path}`
  return `${getCogoPlatformBase()}${suffix}`
}

/**
 * The dev proxy key authenticates the same-origin `/api/platform` hop and
 * explicit `*-dgx-dev.cogo.xyz` proxies ONLY. Sending it anywhere else is
 * both useless and harmful: production
 * `platform.cogo.xyz` rejects the header at the CORS allow-list, so every
 * cross-origin studio call carrying it fails the preflight (measured
 * 2026-07-30 — the deployed studio could not open server projects). Gating
 * on a dev-proxy target also keeps the key out of production bundles that
 * never talk to a dev proxy. The relative path is safe because the browser
 * sends it only to Studio's own origin; Vite/appliance routing performs the
 * cross-host hop.
 */
function isCogoDevProxyTarget(base = getCogoPlatformBase()): boolean {
  return /^\/api\/platform(?:\/|$)/i.test(base)
    || /^https?:\/\/[^/]*-dgx-dev\.cogo\.xyz(?::\d+)?$/i.test(base)
    || /^https?:\/\/localhost(?::\d+)?$/i.test(base)
    || /^https?:\/\/127\.0\.0\.1(?::\d+)?$/i.test(base)
}

export function getCogoDevProxyKey(base?: string): string | undefined {
  if (!isCogoDevProxyTarget(base)) return undefined
  const key = readStudioEnv('VITE_COGO_DEV_PROXY_KEY')?.trim()
  return key || undefined
}

export function withCogoDevProxyHeaders(init?: HeadersInit, base?: string): Headers {
  const headers = new Headers(init)
  const key = getCogoDevProxyKey(base)
  if (key) headers.set('x-cogo-dev-key', key)
  return headers
}

export function withCogoDevProxyHeaderRecord(
  init: Record<string, string> = {},
  base?: string,
): Record<string, string> {
  const key = getCogoDevProxyKey(base)
  return key ? { ...init, 'x-cogo-dev-key': key } : init
}

function stripTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '')
}
